import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import MeetupLocationPicker from '../components/MeetupLocationPicker';
import PreferredLocationsManager from '../components/PreferredLocationsManager';

function SaveAsTemplateInline({ form, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await API.post('/users/me/templates', { name: name.trim(), data: form });
      onSaved(data.template);
      setOpen(false); setName('');
      setMsg('✅ Saved!');
    } catch { setMsg('Failed'); }
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  }

  if (!open) return (
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>💾 Save as Template</button>
      {msg && <span style={{ fontSize:12, color:'#2D7A3A' }}>{msg}</span>}
    </div>
  );
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      <input
        value={name} onChange={e => setName(e.target.value)} placeholder="Template name"
        onKeyDown={e => e.key === 'Enter' && save()}
        autoFocus
        style={{ padding:'4px 8px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, width:160 }}
      />
      <button className="btn btn-cherry btn-sm" onClick={save} disabled={saving || !name.trim()}>Save</button>
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>✕</button>
    </div>
  );
}

const STEPS = ['Type', 'Details', 'Photos', 'Pricing', 'Preview'];
const CONDITIONS = [
  { value:'new',       label:'New',       desc:'Brand new, unused, in original packaging' },
  { value:'like_new',  label:'Like New',  desc:'Used once or twice, perfect condition' },
  { value:'excellent', label:'Excellent', desc:'Used but in near-perfect condition' },
  { value:'very_good', label:'Very Good', desc:'Minor wear, fully functional' },
  { value:'good',      label:'Good',      desc:'Noticeable wear, works perfectly' },
  { value:'fair',      label:'Fair',      desc:'Heavy use, fully functional' },
];

export default function ListItemPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();
  const [step, setStep] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [published, setPublished]   = useState(false);
  const [savedAsDraft, setSavedAsDraft] = useState(false);
  const [publishedId, setPublishedId] = useState(null);
  const [publishedSlug, setPublishedSlug] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);

  const [form, setForm] = useState({
    listingType: 'sale',
    categoryId: '',
    title: '', description: '', condition: '', brand: '', model: '', year: '',
    price: '', weeklyRate: '', depositAmount: '', minRentalDays: 1, maxRentalDays: 30,
    startingBid: '', reservePrice: '', auctionDays: 7,
    buyItNowEnabled: false, buyItNowPrice: '',
    secondChanceEnabled: false,
    rentToBuyEnabled: false, rentToBuyPrice: '',
    quantity: 1,
    locationCity: user?.locationCity || '', locationState: user?.locationState || '', locationZip: '',
    shippingAvailable: false, localPickupOnly: true, freeShipping: false,
    weightLbs: '', lengthIn: '', widthIn: '', heightIn: '',
    instantBooking: true, idRequired: false,
    listingCouponCode: '', listingCouponType: 'fixed', listingCouponAmount: '',
    barterWants: '',
  });

  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);   // { id, url, thumbUrl, file }
  const [errors, setErrors] = useState({});
  const [preferredLocations, setPreferredLocations] = useState([]);
  const [showLocationManager, setShowLocationManager] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState('');

  useEffect(() => { API.get('/categories').then(r => setCategories(r.data.categories)).catch(()=>{}); }, []);
  useEffect(() => { if (user) API.get('/users/me/templates').then(r => setTemplates(r.data.templates || [])).catch(()=>{}); }, [user]);

  // Pre-fill from template if navigated here with templateData state
  useEffect(() => {
    const td = location.state?.templateData;
    if (td) setForm(p => ({ ...p, ...td }));
  }, []); // eslint-disable-line

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleAiAnalyze = useCallback(async (file) => {
    setAiLoading(true);
    setAiError('');
    setAiResult(null);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      // Upload concurrently so it becomes listing photo too
      const fd = new FormData();
      fd.append('image', file);
      API.post('/upload/listing-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(({ data }) => setImages(prev => prev.find(i => i.id === data.id) ? prev : [{ ...data, preview: URL.createObjectURL(file) }, ...prev]))
        .catch(() => {});
      const { data } = await API.post('/ai-assist/listing', { imageBase64: base64, mediaType: file.type || 'image/jpeg' });
      setAiResult(data.suggestion);
    } catch (err) {
      setAiError(err.response?.data?.error || 'AI analysis failed — try again');
    } finally { setAiLoading(false); }
  }, []);

  const onDrop = useCallback(async (files) => {
    for (const file of files.slice(0, 10 - images.length)) {
      const fd = new FormData();
      fd.append('image', file);
      try {
        const { data } = await API.post('/upload/listing-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setImages(prev => [...prev, { ...data, preview: URL.createObjectURL(file) }]);
      } catch {}
    }
  }, [images]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { 'image/*': [] }, maxSize: 10 * 1024 * 1024 });

  const removeImage = (id) => setImages(prev => prev.filter(i => i.id !== id));

  const validateStep = () => {
    const e = {};
    if (step === 1) {
      if (!form.title.trim())       e.title = 'Title is required';
      if (!form.description.trim()) e.description = 'Description is required';
      if (!form.condition)          e.condition = 'Condition is required';
      if (!form.categoryId)         e.categoryId = 'Category is required';
    }
    if (step === 3) {
      if (form.listingType === 'sale'    && !form.price)        e.price = 'Price is required';
      if (form.listingType === 'rent'    && !form.price)        e.price = 'Daily rate is required';
      if (form.listingType === 'auction' && !form.startingBid)  e.startingBid = 'Starting bid is required';
      if (form.listingType === 'barter'  && !form.price)        e.price = 'Estimated value required';
      // Only require locations if local pickup is the ONLY option
      if (form.localPickupOnly && !form.shippingAvailable && preferredLocations.length === 0) {
        e.locations = 'Add at least 1 preferred meetup location';
      }
    }
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleNext = () => { if (validateStep()) setStep(s => s + 1); };
  const handleBack = () => setStep(s => s - 1);

  const handleSaveDraft = async () => {
    if (!form.title) { setErrors({ submit: 'Add a title before saving as draft' }); return; }
    setSavingDraft(true);
    try {
      const toNum = v => (v === '' || v == null) ? null : (parseFloat(v) || null);
      const payload = {
        ...form,
        status: 'draft',
        price: parseFloat(form.price)||0,
        weeklyRate: toNum(form.weeklyRate),
        depositAmount: toNum(form.depositAmount),
        startingBid: toNum(form.startingBid),
        reservePrice: toNum(form.reservePrice),
        buyItNowPrice: toNum(form.buyItNowPrice),
        rentToBuyPrice: toNum(form.rentToBuyPrice),
        listingCouponAmount: form.listingCouponCode ? toNum(form.listingCouponAmount) : null,
        imageIds: images.map(i => i.id),
      };
      const { data } = await API.post('/listings', payload);
      setPublishedId(data.listing.id);
      setPublishedSlug(data.listing.slug);
      setSavedAsDraft(true);
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || 'Failed to save draft' });
    } finally { setSavingDraft(false); }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      // Check subscription and listing limit for non-free listings
      if (form.listingType !== 'free') {
        const countRes = await API.get('/subscriptions/listing-count');
        if (!countRes.data.canListMore) {
          setErrors({
            submit: 'Listing limit reached. You can create up to 3 listings on the free tier. Upgrade to Premium ($25/month) for unlimited listings.',
            upgrade: true
          });
          setPublishing(false);
          return;
        }
      }

      const payload = {
        ...form,
        price: parseFloat(form.price)||0,
        startingBid: parseFloat(form.startingBid)||0,
        reservePrice: parseFloat(form.reservePrice)||0,
        depositAmount: parseFloat(form.depositAmount)||0,
        weeklyRate: parseFloat(form.weeklyRate)||0,
        buyItNowPrice: parseFloat(form.buyItNowPrice)||0,
        rentToBuyPrice: parseFloat(form.rentToBuyPrice)||0,
        listingCouponAmount: form.listingCouponCode ? parseFloat(form.listingCouponAmount)||0 : null,
        auctionEndsAt: form.listingType === 'auction'
          ? new Date(Date.now() + form.auctionDays * 86400000).toISOString()
          : undefined,
        imageIds: images.map(i => i.id),
      };
      const { data } = await API.post('/listings', payload);
      const listingId = data.listing.id;
      setPublishedId(listingId);

      // Save preferred locations if local pickup is enabled
      if (form.localPickupOnly && preferredLocations.length > 0) {
        console.log('Saving preferred locations:', preferredLocations);
        await API.post(`/locations/listings/${listingId}/preferred`, {
          locations: preferredLocations,
        });
        console.log('Preferred locations saved successfully');
      }

      setPublished(true);
    } catch (err) {
      console.error('Publish error:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to publish';
      setErrors({ submit: errorMsg });
    } finally { setPublishing(false); }
  };

  if (!user) {
    return (
      <div style={{ maxWidth:520, margin:'80px auto', textAlign:'center', padding:24 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🔐</div>
        <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, marginBottom:12 }}>Sign in to list an item</h2>
        <button className="btn btn-cherry" onClick={() => navigate('/login')}>Sign in</button>
      </div>
    );
  }

  if (savedAsDraft) {
    return (
      <div style={{ maxWidth:520, margin:'80px auto', textAlign:'center', padding:24 }}>
        <div style={{ fontSize:64, marginBottom:16 }}>📝</div>
        <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, marginBottom:8 }}>Draft Saved</h2>
        <p style={{ color:'var(--text-mid)', marginBottom:24 }}>"{form.title}" has been saved as a draft. You can publish it anytime from your dashboard.</p>
        <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
          <button className="btn btn-cherry" onClick={() => navigate('/dashboard/listings')}>View My Drafts</button>
          <button className="btn btn-ghost" onClick={() => navigate('/sell')}>List Another Item</button>
        </div>
      </div>
    );
  }

  if (published) {
    return (
      <div style={{ maxWidth:680, margin:'0 auto', padding:'40px 24px 80px' }}>
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ fontSize:64, marginBottom:16 }}>🎉</div>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:32, fontWeight:700, marginBottom:12 }}>Your listing is live!</h1>
          <p style={{ fontSize:15, color:'var(--text-mid)' }}>"{form.title}" has been published and is now visible to buyers near you.</p>
        </div>

        <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:16, padding:24, marginBottom:24 }}>
          <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:12 }}>📍 Set Up Meetup Locations</h3>
          <p style={{ fontSize:14, color:'var(--text-mid)', marginBottom:16 }}>
            Optional: Let buyers know where you prefer to meet. You can add up to 3 preferred locations. Buyers can accept one of your locations or suggest an alternative.
          </p>
          {publishedId && (
            <PreferredLocationsManager
              listingId={publishedId}
              onLocationsChanged={(locs) => console.log('Locations saved:', locs)}
            />
          )}
        </div>

        <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
          <button className="btn btn-cherry" onClick={() => navigate(`/listing/${publishedSlug || publishedId}`)}>View Listing</button>
          <button className="btn btn-ghost" onClick={() => {
            setStep(0); setPublished(false); setImages([]); setErrors({});
            setForm({ listingType:'sale', categoryId:'', title:'', description:'', condition:'', brand:'', model:'', year:'', price:'', weeklyRate:'', depositAmount:'', minRentalDays:1, maxRentalDays:30, startingBid:'', reservePrice:'', auctionDays:7, buyItNowEnabled:false, buyItNowPrice:'', secondChanceEnabled:false, rentToBuyEnabled:false, rentToBuyPrice:'', quantity:1, locationCity:user?.locationCity||'', locationState:user?.locationState||'', locationZip:'', shippingAvailable:false, localPickupOnly:true, freeShipping:false, instantBooking:true, idRequired:false, listingCouponCode:'', listingCouponType:'fixed', listingCouponAmount:'', barterWants:'' });
          }}>List Another</button>
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  // Step progress indicator
  const StepBar = () => (
    <div style={{ display:'flex', alignItems:'center', marginBottom:36 }}>
      {STEPS.map((label, i) => (
        <div key={label} style={{ display:'flex', alignItems:'center', flex:1 }}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
            <div style={{ width:32, height:32, borderRadius:'50%', border:`2px solid ${i<=step?'var(--cherry)':'var(--border)'}`, background:i<step?'var(--cherry)':i===step?'var(--cherry)':'white', color:i<=step?'white':'var(--text-soft)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, transition:'all .3s' }}>
              {i < step ? '✓' : i+1}
            </div>
            <div style={{ fontSize:11, fontWeight:600, color:i===step?'var(--cherry)':'var(--text-soft)', whiteSpace:'nowrap' }}>{label}</div>
          </div>
          {i < STEPS.length-1 && <div style={{ flex:1, height:2, background:i<step?'var(--cherry)':'var(--border)', margin:'0 4px', marginBottom:18, transition:'background .3s' }}/>}
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ maxWidth:680, margin:'0 auto', padding:'40px 24px 80px' }}>
      <div style={{ textAlign:'center', marginBottom:32 }}>
        <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:32, fontWeight:700, marginBottom:8 }}>List an Item</h1>
        <p style={{ fontSize:14, color:'var(--text-soft)' }}>Rent it, auction it, or sell it in under 5 minutes</p>
      </div>

      <StepBar />

      {errors.submit && (
        <div className="alert alert-error" style={{ marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span>{errors.submit}</span>
          {errors.upgrade && (
            <button
              className="btn btn-sm btn-cherry"
              onClick={() => navigate('/dashboard/subscription')}
              style={{ marginLeft:12 }}
            >
              Upgrade Now
            </button>
          )}
        </div>
      )}

      {/* STEP 0 — Type */}
      {step === 0 && (
        <div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, marginBottom:20 }}>What are you listing?</h2>
          <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:32 }}>
            {[
              { value:'sale',    icon:'🟢', label:'Sell it',    desc:'Set a fixed price and sell your item outright' },
              { value:'rent',    icon:'🔵', label:'Rent it out', desc:'Earn recurring income by renting your item by the day' },
              { value:'auction', icon:'🔴', label:'Auction it', desc:'Let bidders compete and drive the price up' },
              { value:'barter',  icon:'🔄', label:'Barter it',  desc:'Trade your item for something you want (requires $10 or $20/mo upgrade)' },
              { value:'free',    icon:'🆓', label:'Give it away', desc:'Share for free with your community' },
            ].map(t => (
              <div key={t.value}
                onClick={() => f('listingType', t.value)}
                style={{ display:'flex', alignItems:'center', gap:16, padding:'18px 20px', borderRadius:14, border:`2px solid ${form.listingType===t.value?'var(--cherry)':'var(--border)'}`, background:form.listingType===t.value?'var(--cherry-light)':'white', cursor:'pointer', transition:'all .2s' }}>
                <div style={{ fontSize:32 }}>{t.icon}</div>
                <div>
                  <div style={{ fontWeight:700, fontSize:16, marginBottom:2 }}>{t.label}</div>
                  <div style={{ fontSize:13, color:'var(--text-mid)' }}>{t.desc}</div>
                </div>
                <div style={{ marginLeft:'auto', width:20, height:20, borderRadius:'50%', border:`2px solid ${form.listingType===t.value?'var(--cherry)':'var(--border)'}`, background:form.listingType===t.value?'var(--cherry)':'white', flexShrink:0 }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* STEP 1 — Details */}
      {step === 1 && (
        <div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, marginBottom:20 }}>Item Details</h2>

          {/* AI Assist Panel */}
          <div style={{ background:'linear-gradient(135deg,#4A0010 0%,#8B0000 100%)', borderRadius:14, padding:'16px 18px', marginBottom:24, color:'white' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:22 }}>✨</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:15 }}>AI Listing Assist</div>
                  <div style={{ fontSize:12, opacity:.8 }}>Upload a photo — Claude AI fills in the details</div>
                </div>
              </div>
              <button onClick={() => { setAiOpen(p=>!p); setAiResult(null); setAiError(''); }}
                style={{ background:'rgba(255,255,255,.18)', border:'none', color:'white', borderRadius:100, padding:'5px 14px', cursor:'pointer', fontSize:12, fontWeight:700 }}>
                {aiOpen ? '✕ Close' : '✨ Try It'}
              </button>
            </div>

            {aiOpen && !aiResult && (
              <div style={{ marginTop:16 }}>
                <div
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleAiAnalyze(f); }}
                  onClick={() => !aiLoading && document.getElementById('ai-file-input').click()}
                  style={{ border:'2px dashed rgba(255,255,255,.4)', borderRadius:10, padding:'28px 20px', textAlign:'center', cursor: aiLoading ? 'default' : 'pointer' }}>
                  <input id="ai-file-input" type="file" accept="image/*" style={{ display:'none' }}
                    onChange={e => { if (e.target.files[0]) handleAiAnalyze(e.target.files[0]); e.target.value=''; }} />
                  {aiLoading ? (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12, fontSize:14 }}>
                      <span className="spinner" style={{ borderTopColor:'white', width:20, height:20 }} />
                      Analyzing with Claude AI...
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize:36, marginBottom:8 }}>📸</div>
                      <div style={{ fontWeight:600, fontSize:14 }}>Drop a photo or click to upload</div>
                      <div style={{ fontSize:12, opacity:.75, marginTop:4 }}>AI suggests title, description, condition, category & price</div>
                    </>
                  )}
                </div>
                {aiError && <div style={{ background:'rgba(255,0,0,.25)', borderRadius:8, padding:'8px 12px', fontSize:13, marginTop:10 }}>{aiError}</div>}
              </div>
            )}

            {aiResult && aiOpen && (
              <div style={{ background:'rgba(255,255,255,.96)', borderRadius:10, padding:16, marginTop:16, color:'#333' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                  <span style={{ fontWeight:700, fontSize:14 }}>✨ AI Suggestions</span>
                  <span style={{ fontSize:12, color:'var(--text-soft)', background:'#f0f0f0', borderRadius:100, padding:'2px 8px' }}>
                    Confidence: {aiResult.confidence}
                  </span>
                </div>
                {[
                  { key:'title',       label:'Title',       val: aiResult.title },
                  { key:'description', label:'Description', val: aiResult.description?.slice(0,100) + (aiResult.description?.length>100?'…':'') },
                  { key:'condition',   label:'Condition',   val: aiResult.condition?.replace('_',' ') },
                  { key:'category',    label:'Category',    val: categories.find(c=>c.id===aiResult.categoryId)?.label || aiResult.categorySlug },
                  { key:'price',       label:'Price Range', val: aiResult.suggestedPrice ? `$${aiResult.suggestedPrice} (est. $${aiResult.priceMin}–$${aiResult.priceMax})` : null },
                  { key:'brand',       label:'Brand',       val: aiResult.brand },
                ].filter(r => r.val).map(row => (
                  <div key={row.key} style={{ padding:'7px 0', borderBottom:'1px solid #eee' }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{row.label}</div>
                    <div style={{ fontSize:13, marginTop:2 }}>{row.val}</div>
                  </div>
                ))}
                {aiResult.tags?.length > 0 && (
                  <div style={{ padding:'7px 0', borderBottom:'1px solid #eee' }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Tags</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:4 }}>
                      {aiResult.tags.map(t => <span key={t} style={{ background:'#f0f0f0', borderRadius:100, padding:'2px 8px', fontSize:11 }}>{t}</span>)}
                    </div>
                  </div>
                )}
                <div style={{ display:'flex', gap:8, marginTop:14, flexWrap:'wrap' }}>
                  <button className="btn btn-cherry btn-sm" style={{ flex:1 }} onClick={() => {
                    const s = aiResult;
                    const u = {};
                    if (s.title)          u.title       = s.title;
                    if (s.description)    u.description = s.description;
                    if (s.condition)      u.condition   = s.condition;
                    if (s.categoryId)     u.categoryId  = s.categoryId;
                    if (s.suggestedPrice) u.price       = String(s.suggestedPrice);
                    if (s.brand)          u.brand       = s.brand;
                    setForm(p => ({ ...p, ...u }));
                    setAiResult(null); setAiOpen(false);
                  }}>✅ Apply All Suggestions</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setAiResult(null)}>Try Another Photo</button>
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Category <span className="required">*</span></label>
            <select className={`form-select ${errors.categoryId?'error':''}`} value={form.categoryId} onChange={e=>f('categoryId',e.target.value)}>
              <option value="">Select a category</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
            </select>
            {errors.categoryId && <div className="form-error">{errors.categoryId}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Title <span className="required">*</span></label>
            <input className={`form-input ${errors.title?'error':''}`} placeholder="e.g. Sony A7 IV Mirrorless Camera" value={form.title} onChange={e=>f('title',e.target.value)} maxLength={200} />
            <div className="form-hint">{form.title.length}/200 characters</div>
            {errors.title && <div className="form-error">{errors.title}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Description <span className="required">*</span></label>
            <textarea className={`form-textarea ${errors.description?'error':''}`} placeholder="Describe your item in detail — age, features, what's included, any defects..." value={form.description} onChange={e=>f('description',e.target.value)} style={{ minHeight:120 }} />
            {errors.description && <div className="form-error">{errors.description}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Condition <span className="required">*</span></label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {CONDITIONS.map(c => (
                <div key={c.value} onClick={() => f('condition', c.value)}
                  style={{ padding:'12px 14px', borderRadius:10, border:`1.5px solid ${form.condition===c.value?'var(--cherry)':'var(--border)'}`, background:form.condition===c.value?'var(--cherry-light)':'white', cursor:'pointer', transition:'all .2s' }}>
                  <div style={{ fontWeight:700, fontSize:13, marginBottom:2 }}>{c.label}</div>
                  <div style={{ fontSize:11, color:'var(--text-soft)' }}>{c.desc}</div>
                </div>
              ))}
            </div>
            {errors.condition && <div className="form-error" style={{ marginTop:6 }}>{errors.condition}</div>}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <div className="form-group">
              <label className="form-label">Brand</label>
              <input className="form-input" placeholder="e.g. Sony" value={form.brand} onChange={e=>f('brand',e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Model</label>
              <input className="form-input" placeholder="e.g. A7 IV" value={form.model} onChange={e=>f('model',e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Year</label>
              <input className="form-input" placeholder="e.g. 2022" type="number" min="1900" max={new Date().getFullYear()} value={form.year} onChange={e=>f('year',e.target.value)} />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" placeholder="Matamoras" value={form.locationCity} onChange={e=>f('locationCity',e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input className="form-input" placeholder="PA" value={form.locationState} onChange={e=>f('locationState',e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">ZIP</label>
              <input className="form-input" placeholder="18336" value={form.locationZip} onChange={e=>f('locationZip',e.target.value)} />
            </div>
          </div>
        </div>
      )}

      {/* STEP 2 — Photos */}
      {step === 2 && (
        <div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, marginBottom:8 }}>Photos</h2>
          <p style={{ fontSize:14, color:'var(--text-soft)', marginBottom:20 }}>Add up to 10 photos. The first photo is your cover image.</p>

          <div {...getRootProps()} style={{ border:`2px dashed ${isDragActive?'var(--cherry)':'var(--border)'}`, borderRadius:14, padding:40, textAlign:'center', cursor:'pointer', background:isDragActive?'var(--cherry-light)':'white', transition:'all .2s', marginBottom:20 }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--cherry)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = isDragActive ? 'var(--cherry)' : 'var(--border)'}
          >
            <input {...getInputProps()} />
            <div style={{ fontSize:40, marginBottom:12 }}>📸</div>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:6 }}>
              {isDragActive ? 'Drop photos here' : 'Click anywhere here to browse, or drag & drop'}
            </div>
            <div style={{ fontSize:13, color:'var(--text-soft)' }}>JPG, PNG, WebP up to 10MB each · up to 10 photos</div>
          </div>

          {images.length > 0 && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
              {images.map((img,i) => (
                <div key={img.id} style={{ position:'relative', aspectRatio:'1', borderRadius:10, overflow:'hidden', border:`2px solid ${i===0?'var(--cherry)':'var(--border)'}` }}>
                  <img src={img.preview || `${process.env.REACT_APP_API_URL?.replace('/api','')}${img.url}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                  {i===0 && <div style={{ position:'absolute',bottom:0,left:0,right:0,background:'var(--cherry)',color:'white',fontSize:10,fontWeight:700,textAlign:'center',padding:3 }}>COVER</div>}
                  <button onClick={() => removeImage(img.id)} style={{ position:'absolute',top:5,right:5,width:22,height:22,borderRadius:'50%',background:'rgba(0,0,0,.7)',border:'none',color:'white',cursor:'pointer',fontSize:12,display:'flex',alignItems:'center',justifyContent:'center' }}>✕</button>
                </div>
              ))}
            </div>
          )}
          {images.length === 0 && <div className="alert alert-info" style={{ marginTop:8 }}>💡 Listings with photos get 3× more views</div>}
        </div>
      )}

      {/* STEP 3 — Pricing */}
      {step === 3 && (
        <div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, marginBottom:20 }}>Pricing & Availability</h2>

          {errors.locations && (
            <div className="alert alert-error" style={{ marginBottom:20 }}>
              ⚠️ {errors.locations}
            </div>
          )}

          {/* Quantity section */}
          <div style={{ background:'#F5F0EB', borderRadius:12, padding:'16px 18px', marginBottom:24 }}>
            <label className="form-label" style={{ marginBottom:12 }}>How many items are you listing? <span className="required">*</span></label>
            <input
              className="form-input"
              type="number"
              min="1"
              max="999"
              value={form.quantity}
              onChange={e => f('quantity', Math.max(1, parseInt(e.target.value) || 1))}
              style={{ maxWidth: 200 }}
            />
            <div className="form-hint" style={{ marginTop:8 }}>
              💡 If you have multiple copies, buyers will see it's available (stock label added to listing)
            </div>
          </div>

          {form.listingType === 'sale' && (
            <div className="form-group">
              <label className="form-label">Sale Price <span className="required">*</span></label>
              <div className="price-input-wrap">
                <span className="price-prefix">$</span>
                <input className={`price-field ${errors.price?'error':''}`} type="number" placeholder="0.00" value={form.price} onChange={e=>f('price',e.target.value)} step="0.01" min="0" />
              </div>
              {errors.price && <div className="form-error">{errors.price}</div>}
              {form.price && <div className="form-hint">You'll receive ${(form.price * 0.9).toFixed(2)} after Cherrypkd's 10% fee</div>}
            </div>
          )}

          {form.listingType === 'rent' && (
            <>
              <div className="form-group">
                <label className="form-label">Daily Rate <span className="required">*</span></label>
                <div className="price-input-wrap">
                  <span className="price-prefix">$</span>
                  <input className={`price-field ${errors.price?'error':''}`} type="number" placeholder="0.00 /day" value={form.price} onChange={e=>f('price',e.target.value)} step="0.01" min="0" />
                </div>
                {errors.price && <div className="form-error">{errors.price}</div>}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div className="form-group">
                  <label className="form-label">Security Deposit</label>
                  <div className="price-input-wrap">
                    <span className="price-prefix">$</span>
                    <input className="price-field" type="number" placeholder="0.00" value={form.depositAmount} onChange={e=>f('depositAmount',e.target.value)} step="0.01" min="0" />
                  </div>
                  <div className="form-hint" style={{ color:'var(--text-soft)' }}>
                    💡 Renters pay this upfront as collateral. Returned when item is safely returned. You keep it if item is not returned.
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Weekly Rate</label>
                  <div className="price-input-wrap">
                    <span className="price-prefix">$</span>
                    <input className="price-field" type="number" placeholder="0.00 /week" value={form.weeklyRate} onChange={e=>f('weeklyRate',e.target.value)} step="0.01" min="0" />
                  </div>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div className="form-group">
                  <label className="form-label">Min Rental Days</label>
                  <input className="form-input" type="number" min="1" value={form.minRentalDays} onChange={e=>f('minRentalDays',parseInt(e.target.value))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Max Rental Days</label>
                  <input className="form-input" type="number" min="1" value={form.maxRentalDays} onChange={e=>f('maxRentalDays',parseInt(e.target.value))} />
                </div>
              </div>

              {/* Rent to Buy option */}
              <div style={{ background:'#E3F2FD', borderRadius:12, padding:'16px 18px', marginTop:16 }}>
                <label style={{ display:'flex', alignItems:'center', gap:12, cursor:'pointer', marginBottom:12 }}>
                  <label className="toggle-wrap">
                    <input type="checkbox" checked={form.rentToBuyEnabled} onChange={e=>f('rentToBuyEnabled',e.target.checked)} />
                    <span className="toggle-slider" />
                  </label>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13 }}>🛍️ Rent-to-Buy Option</div>
                    <div style={{ fontSize:12, color:'var(--text-soft)' }}>Let renters purchase the item after rental</div>
                  </div>
                </label>
                {form.rentToBuyEnabled && (
                  <div className="form-group">
                    <label className="form-label">Purchase Price <span className="required">*</span></label>
                    <div className="price-input-wrap">
                      <span className="price-prefix">$</span>
                      <input className="price-field" type="number" placeholder="0.00" value={form.rentToBuyPrice} onChange={e=>f('rentToBuyPrice',e.target.value)} step="0.01" min="0" />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {form.listingType === 'auction' && (
            <>
              <div className="form-group">
                <label className="form-label">Starting Bid <span className="required">*</span></label>
                <div className="price-input-wrap">
                  <span className="price-prefix">$</span>
                  <input className={`price-field ${errors.startingBid?'error':''}`} type="number" placeholder="0.00" value={form.startingBid} onChange={e=>f('startingBid',e.target.value)} step="0.01" min="0" />
                </div>
                {errors.startingBid && <div className="form-error">{errors.startingBid}</div>}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div className="form-group">
                  <label className="form-label">Reserve Price</label>
                  <div className="price-input-wrap">
                    <span className="price-prefix">$</span>
                    <input className="price-field" type="number" placeholder="Hidden minimum" value={form.reservePrice} onChange={e=>f('reservePrice',e.target.value)} step="0.01" />
                  </div>
                  <div className="form-hint">Optional hidden reserve</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Auction Duration</label>
                  <select className="form-select" value={form.auctionDays} onChange={e=>f('auctionDays',parseFloat(e.target.value))}>
                    <option value="0.125">⚡ Lightning (3 hours)</option>
                    <option value="1">1 day</option>
                    <option value="3">3 days</option>
                    <option value="5">5 days</option>
                    <option value="7">7 days</option>
                    <option value="10">10 days</option>
                    <option value="14">14 days</option>
                  </select>
                </div>
              </div>

              {/* Buy It Now option */}
              <div style={{ background:'#FFF3E0', borderRadius:12, padding:'16px 18px', marginTop:16 }}>
                <label style={{ display:'flex', alignItems:'center', gap:12, cursor:'pointer', marginBottom:12 }}>
                  <label className="toggle-wrap">
                    <input type="checkbox" checked={form.buyItNowEnabled} onChange={e=>f('buyItNowEnabled',e.target.checked)} />
                    <span className="toggle-slider" />
                  </label>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13 }}>💰 Buy It Now Option</div>
                    <div style={{ fontSize:12, color:'var(--text-soft)' }}>Let bidders skip the auction and buy immediately</div>
                  </div>
                </label>
                {form.buyItNowEnabled && (
                  <div className="form-group">
                    <label className="form-label">Buy It Now Price <span className="required">*</span></label>
                    <div className="price-input-wrap">
                      <span className="price-prefix">$</span>
                      <input className="price-field" type="number" placeholder="0.00" value={form.buyItNowPrice} onChange={e=>f('buyItNowPrice',e.target.value)} step="0.01" min="0" />
                    </div>
                  </div>
                )}
              </div>

              {/* Second Chance Offer opt-in */}
              <div style={{ background:'#EAF5EC', borderRadius:12, padding:'16px 18px', marginTop:12 }}>
                <label style={{ display:'flex', alignItems:'center', gap:12, cursor:'pointer' }}>
                  <label className="toggle-wrap">
                    <input type="checkbox" checked={form.secondChanceEnabled} onChange={e=>f('secondChanceEnabled',e.target.checked)} />
                    <span className="toggle-slider" />
                  </label>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13 }}>🎁 Second Chance Offers</div>
                    <div style={{ fontSize:12, color:'var(--text-soft)' }}>If the winner cancels, automatically offer the item to the next highest bidder (up to 3 runners-up)</div>
                  </div>
                </label>
              </div>
            </>
          )}

          {form.listingType === 'barter' && (
            <>
              <div className="form-group">
                <label className="form-label">Estimated Value <span className="required">*</span></label>
                <div className="price-input-wrap">
                  <span className="price-prefix">$</span>
                  <input className="price-field" type="number" placeholder="0.00" value={form.price} onChange={e=>f('price',e.target.value)} step="0.01" min="0" />
                </div>
                <div className="form-hint">What would you value this item at? Helps people offer fair trades.</div>
              </div>
              <div style={{ background:'#F3E5F5', borderRadius:12, padding:'16px 18px', marginTop:16 }}>
                <div style={{ fontSize:13, fontWeight:600, marginBottom:6, color:'#6A1B9A' }}>🔄 Items you'll trade for</div>
                <div style={{ fontSize:12, color:'#7B1FA2', marginBottom:10 }}>List specific items, categories, or describe what you're open to. Be as detailed as possible — buyers will use this to decide if they have a match.</div>
                <textarea
                  className="form-textarea"
                  placeholder="e.g. iPhone 14 Pro, gaming laptops, vintage cameras, road bikes — open to any outdoor gear..."
                  value={form.barterWants}
                  onChange={e=>f('barterWants',e.target.value)}
                  maxLength={1000}
                  style={{ minHeight:100 }}
                />
                <div style={{ fontSize:11, color:'#9C27B0', marginTop:6, textAlign:'right' }}>{form.barterWants.length}/1000</div>
              </div>
            </>
          )}

          {form.listingType === 'free' && (
            <>
              <div style={{ background:'#E8F5E9', borderRadius:12, padding:'16px 18px' }}>
                <div style={{ fontSize:13, fontWeight:600, marginBottom:8, color:'#2E7D32' }}>🆓 Free Item Details</div>
                <div style={{ fontSize:12, color:'var(--text-mid)', lineHeight:1.6 }}>
                  Make sure your item description is clear and honest. Provide condition details and note any wear, damage, or missing parts. Free items must be claimed within 7 days.
                </div>
              </div>
            </>
          )}

          {/* Listing Coupon */}
          <div style={{ background:'#FFF3E0', borderRadius:12, padding:'16px 18px', marginTop:20, marginBottom:20 }}>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:12, color:'#E65100' }}>🎟️ Listing Coupon (Optional)</div>
            <div style={{ fontSize:12, color:'var(--text-soft)', marginBottom:12 }}>Add a discount code for this specific listing only</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
              <div className="form-group">
                <label className="form-label">Coupon Code</label>
                <input className="form-input" placeholder="e.g. SAVE10" value={form.listingCouponCode} onChange={e=>f('listingCouponCode',e.target.value.toUpperCase())} maxLength="50" />
              </div>
              <div className="form-group">
                <label className="form-label">Discount Type</label>
                <select className="form-input" value={form.listingCouponType} onChange={e=>f('listingCouponType',e.target.value)}>
                  <option value="fixed">Fixed amount ($)</option>
                  <option value="percentage">Percentage (%)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Discount Amount</label>
                <input className="form-input" type="number" placeholder="0.00" value={form.listingCouponAmount} onChange={e=>f('listingCouponAmount',e.target.value)} step="0.01" min="0" />
              </div>
            </div>
          </div>

          <div style={{ background:'var(--cream)', borderRadius:12, padding:'16px 18px', marginTop:8 }}>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:10 }}>Delivery Options</div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {[
                { key:'localPickupOnly',   label:'Local pick up',         desc:'Buyer meets you in person' },
                { key:'shippingAvailable', label:'Offer shipping',        desc:'Ship via integrated carriers (USPS, UPS, FedEx)' },
                { key:'freeShipping',      label:'Free shipping',         desc:'You cover the shipping cost' },
                { key:'instantBooking',    label:'Instant booking',       desc:'Auto-confirm without approval' },
                { key:'idRequired',        label:'Require ID verification', desc:'Only verified users can book' },
              ].map(o => (
                <label key={o.key} style={{ display:'flex', alignItems:'center', gap:12, cursor:'pointer' }}>
                  <label className="toggle-wrap">
                    <input type="checkbox" checked={form[o.key]} onChange={e=>f(o.key,e.target.checked)} />
                    <span className="toggle-slider" />
                  </label>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13 }}>{o.label}</div>
                    <div style={{ fontSize:12, color:'var(--text-soft)' }}>{o.desc}</div>
                    {o.key === 'localPickupOnly' && form.localPickupOnly && form.shippingAvailable && (
                      <div style={{ fontSize:11, color:'var(--cherry)', marginTop:4 }}>💡 Buyers will choose their delivery method at checkout</div>
                    )}
                  </div>
                </label>
              ))}
            </div>

            {/* Package dimensions preset */}
            {form.shippingAvailable && (
              <div style={{ marginTop: 16, background: '#F0F4F8', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>📦 Package Dimensions (optional)</div>
                <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 12 }}>
                  Save your typical package size — auto-fills when you generate a shipping label.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[['weightLbs','Weight (lbs)'],['lengthIn','Length (in)'],['widthIn','Width (in)'],['heightIn','Height (in)']].map(([key, lbl]) => (
                    <div key={key}>
                      <label style={{ fontSize: 12, color: 'var(--text-soft)', display: 'block', marginBottom: 3 }}>{lbl}</label>
                      <input className="form-input" type="number" min="0" step="0.1" placeholder="0"
                        value={form[key]} onChange={e => f(key, e.target.value)} style={{ fontSize: 13 }} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {form.localPickupOnly && (
            <div style={{ marginTop:24, paddingTop:24, borderTop:'1px solid var(--border)' }}>
              <div style={{ background:'#E3F2FD', borderRadius:12, padding:'16px 18px', marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:14, marginBottom:8, color:'#1565C0' }}>
                  📍 Preferred Meetup Locations {!form.shippingAvailable && <span style={{color:'#C62828'}}>*</span>}
                </div>
                <p style={{ fontSize:13, color:'#1565C0', margin:0 }}>
                  {form.shippingAvailable
                    ? 'Optional: Add up to 3 meetup locations. If buyers choose local pickup, they can accept one of your locations or suggest an alternative.'
                    : 'Add at least 1 location (up to 3) where you prefer to meet buyers. They can accept one of your locations or suggest an alternative.'
                  }
                </p>
              </div>

              {preferredLocations.length > 0 ? (
                <div style={{ background:'#E8F5E9', borderRadius:12, padding:'16px', border:'1px solid #4CAF50', marginBottom:16 }}>
                  <div style={{ fontWeight:700, fontSize:13, marginBottom:12, color:'#2E7D32' }}>✅ Locations Set</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:12 }}>
                    {preferredLocations.map((loc, idx) => (
                      <div key={idx} style={{ fontSize:13, color:'#333' }}>
                        #{idx+1} <strong>{loc.name}</strong> - {loc.address}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowLocationManager(true)}
                    style={{
                      background:'transparent',
                      border:'1px solid #4CAF50',
                      color:'#4CAF50',
                      padding:'8px 12px',
                      borderRadius:6,
                      fontWeight:600,
                      fontSize:12,
                      cursor:'pointer'
                    }}>
                    📝 Edit Locations
                  </button>
                </div>
              ) : (
                <div style={{ background:'white', borderRadius:12, padding:'16px', border:'1px solid var(--border)', marginBottom:16 }}>
                  <p style={{ fontSize:13, color:'var(--text-soft)', marginTop:0, marginBottom:12 }}>
                    💡 Add your preferred locations now so buyers know where to meet you
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowLocationManager(true)}
                    style={{
                      background:'var(--cherry)',
                      color:'white',
                      padding:'10px 16px',
                      borderRadius:6,
                      fontWeight:600,
                      fontSize:13,
                      border:'none',
                      cursor:'pointer'
                    }}>
                    📍 Add Preferred Locations
                  </button>
                  <p style={{ fontSize:12, fontStyle:'italic', color:'var(--text-soft)', margin:'12px 0 0 0' }}>
                    Or add them after publishing if you prefer.
                  </p>
                </div>
              )}

              {/* Location Manager Modal */}
              {showLocationManager && form.localPickupOnly && (
                <div style={{
                  position:'fixed',
                  top:0,
                  left:0,
                  right:0,
                  bottom:0,
                  background:'rgba(0,0,0,0.5)',
                  display:'flex',
                  alignItems:'center',
                  justifyContent:'center',
                  zIndex:2000,
                  padding:20,
                  overflow:'auto'
                }}>
                  <div style={{
                    background:'white',
                    borderRadius:12,
                    maxWidth:700,
                    width:'100%',
                    maxHeight:'72vh',
                    overflow:'auto',
                    padding:16,
                    position:'relative'
                  }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowLocationManager(false);
                        setShowLocationPicker(false);
                      }}
                      style={{
                        position:'absolute',
                        top:16,
                        right:16,
                        background:'none',
                        border:'none',
                        fontSize:24,
                        cursor:'pointer',
                        color:'#999'
                      }}>
                      ✕
                    </button>

                    <h3 style={{ margin:'0 0 8px 0', fontSize:20, fontWeight:700 }}>
                      📍 Preferred Meetup Locations
                    </h3>

                    {/* Progress bar */}
                    <div style={{ marginBottom:24 }}>
                      <div style={{ display:'flex', gap:6, marginBottom:10 }}>
                        {[1,2,3].map(num => (
                          <div key={num} style={{
                            flex:1,
                            height:8,
                            background: num <= preferredLocations.length ? 'var(--cherry)' : '#e0e0e0',
                            borderRadius:4,
                            transition:'background 0.3s'
                          }} />
                        ))}
                      </div>
                      <div style={{ fontSize:13, color:'var(--text-soft)' }}>
                        {preferredLocations.length}/3 locations added
                      </div>
                    </div>

                    {/* Added locations list */}
                    {preferredLocations.length > 0 && (
                      <div style={{ marginBottom:24 }}>
                        <div style={{ fontWeight:600, fontSize:13, color:'var(--text-soft)', marginBottom:12 }}>
                          ✅ Your Locations
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                          {preferredLocations.map((loc, idx) => (
                            <div key={idx} style={{
                              background:'#E8F5E9',
                              border:'1px solid #4CAF50',
                              borderRadius:8,
                              padding:12,
                              display:'flex',
                              justifyContent:'space-between',
                              alignItems:'flex-start'
                            }}>
                              <div style={{ flex:1 }}>
                                <div style={{ fontWeight:700, fontSize:14, marginBottom:4, color:'#2E7D32' }}>
                                  Location #{idx+1}
                                </div>
                                <div style={{ fontWeight:600, fontSize:14, marginBottom:2, color:'#333' }}>{loc.name}</div>
                                <div style={{ fontSize:12, color:'var(--text-soft)' }}>{loc.address?.substring(0, 70)}</div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setPreferredLocations(p => p.filter((_, i) => i !== idx))}
                                style={{
                                  background:'none',
                                  border:'none',
                                  color:'#c33',
                                  fontSize:18,
                                  cursor:'pointer',
                                  padding:'0 8px',
                                  flexShrink:0
                                }}
                                title="Remove this location">
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Location picker or button to start */}
                    {!showLocationPicker && preferredLocations.length < 3 && (
                      <div style={{ marginBottom:20 }}>
                        <button
                          type="button"
                          onClick={() => setShowLocationPicker(true)}
                          style={{
                            width:'100%',
                            padding:'14px',
                            background:'var(--cherry)',
                            color:'white',
                            border:'none',
                            borderRadius:8,
                            fontWeight:700,
                            fontSize:15,
                            cursor:'pointer'
                          }}>
                          📍 Select Location #{preferredLocations.length + 1}
                        </button>
                      </div>
                    )}

                    {/* Show picker when user clicks the button */}
                    {showLocationPicker && preferredLocations.length < 3 && (
                      <div style={{ marginBottom:20, paddingBottom:16, borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', paddingTop:16 }}>
                        <div style={{ fontWeight:600, fontSize:13, color:'var(--text-soft)', marginBottom:12 }}>
                          Search or click on the map for Location #{preferredLocations.length + 1}
                        </div>
                        <MeetupLocationPicker
                          onSelect={(location) => {
                            setPreferredLocations(p => [...p, location]);
                            setShowLocationPicker(false);
                          }}
                          initialLocation={null}
                          listingLocation={null}
                          autoConfirm={true}
                        />
                      </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ display:'flex', gap:12, paddingTop:12, borderTop:'1px solid var(--border)' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setShowLocationManager(false);
                          setShowLocationPicker(false);
                        }}
                        style={{
                          flex:1,
                          padding:'12px',
                          background:'white',
                          border:'1px solid var(--border)',
                          borderRadius:6,
                          fontWeight:600,
                          cursor:'pointer'
                        }}>
                        {preferredLocations.length === 0 ? 'Skip for Now' : 'Done'}
                      </button>
                      {preferredLocations.length > 0 && preferredLocations.length < 3 && (
                        <button
                          type="button"
                          onClick={() => setShowLocationPicker(false)}
                          style={{
                            flex:1,
                            padding:'12px',
                            background:'#f0f0f0',
                            color:'#333',
                            border:'none',
                            borderRadius:6,
                            fontWeight:600,
                            cursor:'pointer'
                          }}>
                          Add Later
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* STEP 4 — Preview */}
      {step === 4 && (
        <div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, marginBottom:20 }}>Preview & Publish</h2>
          <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:16, overflow:'hidden', marginBottom:24 }}>
            {images[0] && (
              <div style={{ height:220, overflow:'hidden' }}>
                <img src={images[0].preview||`${process.env.REACT_APP_API_URL?.replace('/api','')}${images[0].url}`} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              </div>
            )}
            <div style={{ padding:20 }}>
              <div style={{ fontSize:11, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:5 }}>
                {categories.find(c=>c.id==form.categoryId)?.label}
              </div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, marginBottom:10 }}>{form.title||'Your listing title'}</h3>
              <p style={{ fontSize:14, color:'var(--text-mid)', lineHeight:1.65, marginBottom:14 }}>{form.description?.slice(0,200)}{form.description?.length>200?'...':''}</p>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:14, borderTop:'1px solid var(--border)' }}>
                <div>
                  <div style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:700 }}>
                    {form.listingType === 'free' ? '🆓 Free' : form.listingType === 'barter' ? `$${form.price || '—'}` : `$${form.listingType==='auction' ? form.startingBid : form.price || '—'}`}
                    {form.listingType==='rent'&&<span style={{ fontSize:14,fontWeight:400,color:'var(--text-soft)',fontFamily:"'DM Sans',sans-serif" }}>/day</span>}
                  </div>
                  {form.locationCity && <div style={{ fontSize:12, color:'var(--text-soft)' }}>📍 {form.locationCity}, {form.locationState}</div>}
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <span className={`pill pill-${form.listingType==='sale'?'sale':form.listingType==='rent'?'rent':form.listingType==='barter'?'barter':form.listingType==='free'?'free':'auction'}`}>
                    {form.listingType==='sale'?'For Sale':form.listingType==='rent'?'For Rent':form.listingType==='barter'?'Barter':form.listingType==='free'?'Free':'Auction'}
                  </span>
                  {form.condition && <span className="pill" style={{ background:'var(--cream)', color:'var(--text-mid)' }}>{form.condition.replace('_',' ')}</span>}
                </div>
              </div>
            </div>
          </div>
          <div className="alert alert-info" style={{ marginBottom:20 }}>
            🔒 Your listing will be reviewed for safety. It goes live immediately and can be edited anytime.
          </div>
        </div>
      )}

      {/* Nav buttons */}
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:32, paddingTop:24, borderTop:'1px solid var(--border)', flexWrap:'wrap', gap:10 }}>
        {/* Left side */}
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {step > 0
            ? <button className="btn btn-ghost" onClick={handleBack}>← Back</button>
            : templates.length > 0 && (
                <div style={{ position:'relative' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowTemplates(p=>!p)}>📋 Load Template</button>
                  {showTemplates && (
                    <div style={{ position:'absolute', top:'100%', left:0, background:'white', border:'1px solid var(--border)', borderRadius:10, padding:8, zIndex:50, minWidth:200, boxShadow:'var(--shadow)' }}>
                      {templates.map(t => (
                        <div key={t.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 8px', borderRadius:6, cursor:'pointer' }}>
                          <span style={{ fontSize:13, fontWeight:600 }} onClick={() => { const d = typeof t.data === 'string' ? JSON.parse(t.data) : t.data; setForm(p=>({...p,...d})); setShowTemplates(false); setStep(1); }}>{t.name}</span>
                          <button style={{ fontSize:11, color:'var(--cherry)', background:'none', border:'none', cursor:'pointer' }} onClick={() => { API.delete(`/users/me/templates/${t.id}`); setTemplates(p=>p.filter(x=>x.id!==t.id)); }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
          }
        </div>
        {/* Right side */}
        <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
          {form.title && step > 0 && step < 4 && (
            <SaveAsTemplateInline form={form} onSaved={t => setTemplates(p => [t, ...p])} />
          )}
          {form.title && (
            <button className="btn btn-ghost" onClick={handleSaveDraft} disabled={savingDraft}>
              {savingDraft ? 'Saving...' : '📝 Save Draft'}
            </button>
          )}
          {step < STEPS.length - 1
            ? <button className="btn btn-cherry" onClick={handleNext}>Continue →</button>
            : <button className="btn btn-cherry btn-lg" onClick={handlePublish} disabled={publishing}>
                {publishing ? <><span className="spinner"/>Publishing...</> : '🚀 Publish Listing'}
              </button>
          }
        </div>
      </div>
    </div>
  );
}
